<template lang="pug">
  CommonSection(title="Описание проекта" v-if="project" :back="{ title: project.name, name: 'project-stages', params: { projectId: project.id } }")
    .b-project-desc
      ._main
        FormBlock(mobile)
          ._header
            h1._title1 {{ project.name }}
            EllipsisMenu(:items="ellipsisMenuItems")
          DateProgressbar._date-progress(:dateProgress="dateProgress")
          BaseText(v-if="project.description" v-html="project.description")
          EmptyList(v-else) Нет описания
        FormBlock(mobile)
          h2._title2.-mb Текущие участники проекта
          ._members
            ._member(v-for="member in members")
              UserTile(:user="member.user") {{ member.role }}
              BaseProgressbar._task-progress(v-bind="member.progress")
        ChartTimeLine(v-if="stats.length" :data="stats")
      ._aside
        FormBlock(mobile)._avatar-block
          ._title2.-center Аватар проекта
          AvatarEditable(:avatar="project.avatar" placeholder="no-avatar-project" :editable="false")
        FormBlock(mobile)
          ._title2 Данные проекта
            template(v-if="objectType")
              ._hr
              ._info-title1 Тип помещения:
              ._info-value1 {{ objectType }}
            template(v-if="objectInfo.length")
              ._hr
              ._object-info
                template(v-for="item in objectInfo")
                  ._info-title2 {{ item.title }}
                  ._info-value2 {{ item.value }}
            template(v-if="project.objectAddress")
              ._hr
              ._info-title1 Адрес:
              ._info-value1 {{ project.objectAddress }}
            template(v-if="projectInfo.length")
              ._hr
              ._object-info
                template(v-for="item in projectInfo")
                  ._info-title2 {{ item.title }}
                  ._info-value2 {{ item.value }}
</template>

<script>
import Access from '@/utils/access';
import api from '@/api/project';
import projectStatisticApi from '@/api/projectStatistic';
import utils from '@/utils/common';

import dateProgress from '@/utils/dateProgress';
import taskListModel from '@/model/taskList';
import taskProgress from '@/utils/taskProgress';

import CommonSection from '@/components/CommonSection.vue';
import FormBlock from '@/components/FormBlock.vue';
import BaseText from '@/components/BaseText.vue';
import AvatarEditable from '@/components/AvatarEditable.vue';
import DateProgressbar from '@/components/DateProgressbar.vue';
import EllipsisMenu from '@/components/EllipsisMenu.vue';
import UserTile from '@/components/UserTile.vue';
import BaseProgressbar from '@/components/BaseProgressbar.vue';
import EmptyList from '@/components/ui/EmptyList.vue';
import ChartTimeLine from '@/components/chart/ChartTimeLine.vue';

export default {
  name: 'ProjectDescriptionPage',
  components: {
    BaseProgressbar,
    UserTile,
    EllipsisMenu,
    DateProgressbar,
    BaseText,
    FormBlock,
    CommonSection,
    AvatarEditable,
    EmptyList,
    ChartTimeLine,
  },
  data() {
    return {
      stats: [],
    };
  },
  created() {
    const { projectId } = this.$route.params;
    taskListModel.fetch({ 'stage.project': projectId });
    projectStatisticApi.fetchList({ project: projectId })
      .then(r => { this.stats = r.items; });
  },
  computed: {
    project() { return this.$store.state.project.project; },
    tasks() { return this.$store.state.taskList.items; },
    dateProgress() { return dateProgress(this.project.dateStart, this.project.dateFinish); },
    access() { return new Access(this.project, this.$store.state.auth.user); },
    userCanEdit() { return this.access.isProjectEditable && (this.access.isProjectAuthor() || this.access.isProjectAdmin()); },
    ellipsisMenuItems() {
      const items = [];

      if (this.access.canEditProject()) {
        items.push({
          text: 'Редактировать',
          icon: 'edit',
          route: { name: 'project-edit', params: { projectId: this.project.id } },
        });
      }

      if (!this.project.isArchived && this.access.canArchiveProject()) {
        items.push({
          text: 'В архив',
          icon: 'archive',
          handler: () => { this.archive(true); },
        });
      }

      if (this.project.isArchived && this.access.canUnarchiveProject()) {
        items.push({
          text: 'Восстановить',
          icon: 'archive',
          handler: () => { this.archive(false); },
        });
        items.push({
          text: 'Удалить',
          icon: 'trash',
          handler: () => { this.remove(false); },
        });
      }

      return items;
    },
    members() {
      return [
        { user: this.project.author, role: 'author' },
        ...this.project.projectMembers,
      ].map(m => {
        let { role } = m;

        // eslint-disable-next-line default-case
        switch (role) {
          case 'author':      role = 'Автор';         break;   // eslint-disable-line
          case 'admin':       role = 'Администратор'; break;   // eslint-disable-line
          case 'work':        role = 'Рабочий';       break;   // eslint-disable-line
          case 'standard':    role = 'Стандартный';   break;   // eslint-disable-line
        }

        const tasksOfUser = this.tasks.filter(t => t.manager?.['@id'] === m.user['@id']);

        const tasksTotal = tasksOfUser.length;
        const tasksClosed = tasksOfUser.filter(t => t.isClosed).length;
        const progress = taskProgress(tasksTotal, tasksClosed);

        return { ...m, role, progress };
      });
    },
    objectType() {
      switch (this.project.objectType) {
        case 'apartment':   return 'Квартира';                    // eslint-disable-line
        case 'flat':        return 'Многоквартирный дом';         // eslint-disable-line
        case 'house':       return 'Частный дом';                 // eslint-disable-line
        case 'social':      return 'Социальный объект';           // eslint-disable-line
        case 'commercial':  return 'Коммерческая недвижимость';   // eslint-disable-line
        case 'other':       return 'Другое';                      // eslint-disable-line
        default:            return '';                            // eslint-disable-line
      }
    },
    objectInfo() {
      return [
        { title: 'Площадь, м²',           value: this.project.objectArea },           // eslint-disable-line
        { title: 'руб/м²',                value: this.project.pricePerSquareMeter },  // eslint-disable-line
        { title: 'Этажей',                value: this.project.objectFloorsQty },      // eslint-disable-line
        { title: 'Количество комнат',     value: this.project.objectRoomsQty },       // eslint-disable-line
      ].filter(o => o.value);
    },
    projectInfo() {
      return [
        { title: 'Объем проекта',    value: utils.fileSize(this.project.fileInfo.sizeOfFiles).string },  // eslint-disable-line
        { title: 'Кол-во папок',     value: this.project.fileInfo.numberOfCategories                 },  // eslint-disable-line
        { title: 'Кол-во файлов',    value: this.project.fileInfo.numberOfFiles                      },  // eslint-disable-line
        { title: 'Кол-во разделов',  value: this.project.taskInfo.numberOfStages                     },   // eslint-disable-line
        { title: 'Всего задач',      value: this.project.taskInfo.numberOfTasks                      },   // eslint-disable-line
        { title: 'Открытых задач',   value: this.project.taskInfo.numberOfOpenedTasks                },   // eslint-disable-line
        { title: 'Закрытых задач',   value: this.project.taskInfo.numberOfClosedTasks                },   // eslint-disable-line
      ];
    },
  },
  methods: {
    sizeStr(v) { return utils.fileSize(v).string; },
    archive(isArchive) {
      api.archive(this.project.id, isArchive, true).then(() => { this.$store.commit('project/setProjectArchived', isArchive); });
    },
    async remove() {
      if (await this.$confirm(`Вы действительно хотите удалить проект [${this.project.name}]?`)) {
        api.delete(this.project.id, true)
          .then(() => { this.$router.push({ name: 'project-list' }); });
      }
    },
  },
};
</script>

<style lang="scss">
.project-desc {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 38px;
  align-items: start;

  &__title1 {
    font-weight: 500;
    font-size: 24px;
    line-height: 30px;
    color: #1E3147;
  }

  &__title2 {
    font-weight: 500;
    font-size: 18px;
    line-height: 24px;
    color: #232226;

    &--center {
      text-align: center;
    }

    &--mb {
      margin-bottom: 20px;
    }
  }

  &__main {
    display: grid;
    gap: 38px;
  }

  &__aside {
    display: grid;
    gap: 38px;
  }

  &__avatar-block {
    display: grid;
    gap: 13px;
    justify-content: center;
  }

  &__date-progress {
    margin: 12px 0 38px;
    width: 352px;
    max-width: 100%;
  }

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }

  &__hr {
    margin: 20px 0;
    height: 1px;
    width: 100%;
    background: #EBEFF2;
  }

  &__object-info {
    display: grid;
    grid-template-columns: max-content 1fr;
    align-items: center;
    width: 100%;
  }

  &__info-title1 {
    font-weight: 500;
    font-size: 14px;
    line-height: 12px;
    letter-spacing: 0.01em;
    margin-bottom: 14px;
    color: #92A0B0;
  }

  &__info-title2 {
    font-weight: 500;
    font-size: 14px;
    line-height: 12px;
    letter-spacing: 0.01em;
    color: #92A0B0;
    margin: 7px 0;
  }

  &__info-value1 {
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0.01em;
    color: #1E3147;
    padding: 6px 11px;
    background: #F5F7F9;
    border-radius: 10px;
  }

  &__info-value2 {
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0.01em;
    color: #1E3147;
    justify-self: end;
  }

  &__member {
    padding: 7px 23px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    &:nth-child(odd) {
      background: #F5F7F9;
      border-radius: 6px;
    }
  }

  &__task-progress {
    width: 352px;
    max-width: 100%;
  }

  @media (max-width: 1250px) {
    grid-template-columns: 1fr;

    &__aside {
      grid-template-columns: 1fr 1fr;
      align-items: start;
    }
  }

  @media (max-width: 900px) {
    &__member {
      flex-direction: column;
      gap: 20px;
      align-items: stretch;
      padding: 14px 20px;
    }

    &__task-progress {
      width: 100%;
    }

    &__date-progress {
      width: 100%;
    }
  }

  @media (max-width: 768px) {
    &__aside {
      grid-template-columns: 1fr;
    }
  }
}
</style>
